// Floating photo-upload progress indicator, rendered once at the app root
// (inside Layout) so it stays visible while the technician moves around the
// portal. Backed by the global UploadQueueContext. Renders nothing when the
// queue is empty.

import { useUploadQueue } from "../context/UploadQueueContext";
import { X } from "lucide-react";

export default function UploadProgressWidget() {
  const { tasks, retryFailed, clearCompleted, dismissAll } = useUploadQueue();

  if (!tasks.length) return null;

  const total = tasks.length;
  const done = tasks.filter((t) => t.status === "done").length;
  const failed = tasks.filter((t) => t.status === "error");
  const errored = failed.length;
  const active = tasks.some((t) => t.status === "pending" || t.status === "uploading");
  const finished = done + errored;
  const pct = total ? Math.round((finished / total) * 100) : 0;
  // Surface the actual failure reason (there's no console on an iPhone).
  const errorMsg = errored > 0 ? failed[0].error : null;

  let heading;
  if (active) heading = `Uploading files… ${finished} of ${total}`;
  else if (errored) heading = `${done} uploaded · ${errored} failed`;
  else heading = `${done} file${done === 1 ? "" : "s"} uploaded`;

  return (
    <div className="fixed bottom-6 sm:bottom-4 right-4 z-top w-72 max-w-[calc(100vw-2rem)]">
      <div className="bg-surface border border-line rounded-xl shadow-2xl overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-ink truncate">{heading}</p>
            {active && (
              <p className="text-xs text-ink-muted mt-0.5">
                You can keep working — uploads continue in the background.
              </p>
            )}
          </div>
          {!active && (
            <button
              type="button"
              onClick={dismissAll}
              aria-label="Dismiss"
              className="inline-flex items-center justify-center text-ink-muted hover:text-ink text-lg leading-none -mt-0.5"
            ><X className="w-5 h-5" aria-hidden="true" /></button>
          )}
        </div>

        <div className="px-4 pb-3">
          <div className="h-1.5 w-full bg-surface-inset rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                errored && !active ? "bg-danger-500" : "bg-brand-600"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>

          {errorMsg && !active && (
            <p className="text-xs text-danger-700 mt-2 break-words line-clamp-3">
              {errored > 1 ? `${errored} failed. ` : ""}Error: {errorMsg}
            </p>
          )}

          {(errored > 0 || (!active && done > 0)) && (
            <div className="flex items-center gap-3 mt-2.5">
              {errored > 0 && (
                <button
                  type="button"
                  onClick={retryFailed}
                  className="text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  Retry {errored} failed
                </button>
              )}
              {!active && done > 0 && (
                <button
                  type="button"
                  onClick={clearCompleted}
                  className="text-xs font-medium text-ink-muted hover:text-ink"
                >
                  Clear completed
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
